/**
 * Evidence Numbers API service
 *
 * Provides functions for the central evidence registry.
 */

import { apiClient } from './api'
import type {
  EvidenceEndpointIds,
  EvidenceNumber,
  EvidenceNumberFilter,
  EvidenceNumberType,
  EvidenceStatusLogEntry,
  EvidenceSummary,
  PaginatedEvidenceNumbers,
  RegisterBatchRequest,
  RegisterEvidenceNumberRequest,
} from '../types/evidence-numbers'

function buildQueryString(params: object): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value))
    }
  }
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

/**
 * List evidence numbers with filtering and pagination
 */
export async function listEvidenceNumbers(
  caseId: string,
  filter?: EvidenceNumberFilter
): Promise<PaginatedEvidenceNumbers> {
  const query = filter ? buildQueryString(filter) : ''
  return apiClient.get<PaginatedEvidenceNumbers>(`/cases/${caseId}/evidence-numbers${query}`)
}

/**
 * Get a single evidence number by ID
 */
export async function getEvidenceNumber(caseId: string, id: string): Promise<EvidenceNumber> {
  return apiClient.get<EvidenceNumber>(`/cases/${caseId}/evidence-numbers/${id}`)
}

/**
 * Lookup evidence number by formatted string
 */
export async function getByNumber(caseId: string, number: string): Promise<EvidenceNumber> {
  return apiClient.get<EvidenceNumber>(
    `/cases/${caseId}/evidence-numbers/by-number/${encodeURIComponent(number)}`
  )
}

/**
 * Get evidence summary counts for dashboard cards
 */
export async function getEvidenceSummary(caseId: string): Promise<EvidenceSummary> {
  return apiClient.get<EvidenceSummary>(`/cases/${caseId}/evidence-numbers/summary`)
}

/**
 * Get the IDs of endpoints that already hold an evidence number.
 *
 * Feeds the Register dialog's set difference against the case's endpoints. Replaces a
 * `perPage: 1000` list call that the backend clamped to 100 rows — on a case with more than 100
 * evidence numbers that under-counted registrations and offered already-registered endpoints for
 * registration again.
 */
export async function getEvidenceEndpointIds(
  caseId: string,
  numberType: EvidenceNumberType = 'base'
): Promise<EvidenceEndpointIds> {
  return apiClient.get<EvidenceEndpointIds>(
    `/cases/${caseId}/evidence-numbers/endpoint-ids?numberType=${encodeURIComponent(numberType)}`
  )
}

/**
 * Get status change history for an evidence number
 */
export async function getStatusHistory(
  caseId: string,
  evidenceNumberId: string
): Promise<EvidenceStatusLogEntry[]> {
  return apiClient.get<EvidenceStatusLogEntry[]>(
    `/cases/${caseId}/evidence-numbers/${evidenceNumberId}/history`
  )
}

/**
 * List collection amendments for a base evidence number
 */
export async function listAmendments(
  caseId: string,
  baseEvidenceNumberId: string
): Promise<EvidenceNumber[]> {
  return apiClient.get<EvidenceNumber[]>(
    `/cases/${caseId}/evidence-numbers/${baseEvidenceNumberId}/amendments`
  )
}

/**
 * Register a base evidence number for an endpoint
 */
export async function registerEvidenceNumber(
  caseId: string,
  request: RegisterEvidenceNumberRequest
): Promise<EvidenceNumber> {
  return apiClient.post<EvidenceNumber>(`/cases/${caseId}/evidence-numbers/register`, request)
}

/**
 * Register base evidence numbers for multiple endpoints
 */
export async function registerBatch(
  caseId: string,
  request: RegisterBatchRequest
): Promise<EvidenceNumber[]> {
  return apiClient.post<EvidenceNumber[]>(
    `/cases/${caseId}/evidence-numbers/register-batch`,
    request
  )
}
